import React, { DispatchWithoutAction, FC, MouseEvent, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fc } from 'i18n';
import { ISVGPersonalProps } from 'types/svg';
import { SvgWrapper } from 'components/atoms/svg-wrapper';
import { LeasesStatuses, TepIntents } from 'constants/tep';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { leasesControlsSelector } from 'core/redux/selectors/tep';
import { actions as leasesActions } from 'core/redux/api/veta/tep/leases';
import { bell, chevronDown, draft, edit2, leaseIssue, returnIcon, undraft, unselectAll } from 'assets/icons';

import styles from './styles.module.css';

interface IOption {
  icon: ISVGPersonalProps;
  label: string;
  action: DispatchWithoutAction;
}

export const ActionsSelect: FC = () => {
  const wrapperRef = useRef(null);

  const dispatch = useDispatch();

  const { count, status } = useSelector(leasesControlsSelector);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useOnClickOutside([wrapperRef], () => setIsOpen(false));

  const openModal = (intent: string) => () => dispatch(leasesActions.intent({ intent }));

  const unselectAllPasses = () => dispatch(leasesActions.select({ leases: [] }));

  const options = useMemo(() => {
    if (status === LeasesStatuses.Mixed) {
      return [{ icon: unselectAll, label: 'unselect all', action: unselectAllPasses }];
    }

    if (status === LeasesStatuses.Available) {
      return [
        { icon: draft, label: 'draft', action: openModal(TepIntents.Draft) },
        { icon: unselectAll, label: 'unselect all', action: unselectAllPasses },
      ];
    }

    if (status === LeasesStatuses.Drafted) {
      return [
        { icon: edit2, label: 'edit', action: openModal(TepIntents.Edit) },
        { icon: leaseIssue, label: 'issue', action: openModal(TepIntents.Issue) },
        { icon: undraft, label: 'undraft', action: openModal(TepIntents.Undraft) },
        { icon: unselectAll, label: 'unselect all', action: unselectAllPasses },
      ];
    }

    if (status === LeasesStatuses.Issued) {
      return [
        { icon: bell, label: 'notify', action: openModal(TepIntents.Notify) },
        { icon: returnIcon, label: 'return', action: openModal(TepIntents.Return) },
        { icon: unselectAll, label: 'unselect all', action: unselectAllPasses },
      ];
    }
  }, [status]);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const selectOption = (option: IOption) => (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    option.action();
    setIsOpen(false);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.content} ref={wrapperRef}>
        <div className={styles.header} onClick={toggleDropdown}>
          <span>{`${fc('selected')} (${count})`}</span>
          <SvgWrapper width={9} height={5} details={chevronDown} />
        </div>
        {isOpen && (
          <ul className={styles.options}>
            {options?.map((option, index) => (
              <li className={styles.option} key={index} onClick={selectOption(option)}>
                <SvgWrapper width={28} height={28} details={option.icon} />
                {fc(option.label)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
